import { toast } from "react-toastify";
import { i18n } from "../translate/i18n";

const TRANSIENT_NETWORK_PATTERNS = [
  /network\s*error/i,
  /service\s*unavailable/i,
  /failed\s*to\s*fetch/i,
  /err_network/i,
  /cors/i,
  /xmlhttprequest/i,
  /request\s*failed\s*with\s*status\s*code\s*50[234]/i,
  /\b50[234]\b/
];

// Funções (não constantes de módulo) para o texto seguir o idioma em uso.
const genericUnavailableMessage = () => i18n.t("toasts.genericUnavailable");
export const genericFallbackMessage = () => i18n.t("toasts.genericFallback");

// Prefixo técnico ("Error:", "Erro:", repetido ou sozinho) no início do texto.
// Só o prefixo: a palavra dentro de uma frase ("Error loading contacts",
// "Error al guardar") faz parte da mensagem e fica.
const TECHNICAL_ERROR_PREFIX = /^(?:Err(?:or|o)\b\s*(?::\s*|$))+/i;
const BACKEND_ERROR_CODE = /^ERR_[A-Z0-9_]+$/;

const translateBackendErrorCode = text => {
  if (typeof text !== "string") {
    return null;
  }

  const code = text.trim();
  if (BACKEND_ERROR_CODE.test(code) && i18n.exists(`backendErrors.${code}`)) {
    return i18n.t(`backendErrors.${code}`);
  }

  return null;
};

// Para telas que guardam err.response.data.error em estado em vez de toast:
// código ERR_ conhecido vira a tradução; texto comum passa direto; vazio vira
// o fallback.
export const translateBackendError = (codeOrText, fallback) => {
  const translated = translateBackendErrorCode(codeOrText);
  if (translated) {
    return translated;
  }

  const text = typeof codeOrText === "string" ? codeOrText.trim() : "";
  return text || fallback;
};

let toastPolicyConfigured = false;

const normalizeText = value => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : "";
};

export const isTransientNetworkIssue = value => {
  if (!value) {
    return false;
  }

  const backendError = value?.response?.data?.error || value?.response?.data?.message || "";
  if (
    value?.response?.status === 503 &&
    /whatsapp\s+indispon[ií]vel\s+para\s+validar\s+o\s+n[uú]mero/i.test(String(backendError))
  ) {
    return false;
  }

  const status = value?.response?.status;
  if ([502, 503, 504].includes(status)) {
    return true;
  }

  if (value?.code === "ERR_NETWORK") {
    return true;
  }

  if (value?.request && !value?.response) {
    const message = normalizeText(value?.message || "");
    return TRANSIENT_NETWORK_PATTERNS.some(pattern => pattern.test(message || ""));
  }

  const asText = normalizeText(
    typeof value === "string"
      ? value
      : value?.message || value?.response?.data?.message || value?.response?.data?.error || ""
  );

  if (!asText) {
    return false;
  }

  return TRANSIENT_NETWORK_PATTERNS.some(pattern => pattern.test(asText));
};

export const sanitizeToastMessage = value => {
  if (typeof value !== "string") {
    return value;
  }

  let message = value.trim();

  if (!message) {
    return genericFallbackMessage();
  }

  message = message
    .replace(/\bAxiosError\s*:\s*/gi, "")
    .trim()
    .replace(TECHNICAL_ERROR_PREFIX, "")
    // Só casa texto em português (o backend e telas antigas gravam "Erro ao ...").
    .replace(/^Erro\s+ao\s+/i, "Não foi possível ")
    .replace(/\s{2,}/g, " ")
    .trim();

  if (!message) {
    return genericFallbackMessage();
  }

  if (TRANSIENT_NETWORK_PATTERNS.some(pattern => pattern.test(message))) {
    return genericUnavailableMessage();
  }

  // Código do backend (ERR_...) sem tradução aplicada: mostra o texto traduzido.
  return translateBackendErrorCode(message) || message;
};

const resolveToastText = input => {
  if (typeof input === "string") {
    return sanitizeToastMessage(input);
  }

  if (input?.message && typeof input.message === "string") {
    return sanitizeToastMessage(input.message);
  }

  if (typeof input?.response?.data === "string") {
    return sanitizeToastMessage(input.response.data);
  }

  if (typeof input?.response?.data?.message === "string") {
    return sanitizeToastMessage(input.response.data.message);
  }

  if (typeof input?.response?.data?.error === "string") {
    return sanitizeToastMessage(input.response.data.error);
  }

  if (input !== undefined && input !== null) {
    return genericFallbackMessage();
  }

  return genericFallbackMessage();
};

const configureToastPolicy = () => {
  if (toastPolicyConfigured) {
    return;
  }

  toastPolicyConfigured = true;

  const originalToastError = toast.error.bind(toast);

  toast.error = (message, options) => {
    if (isTransientNetworkIssue(message)) {
      console.warn("[toastPolicy] Toast tecnico suprimido (indisponibilidade temporaria):", message);
      return null;
    }

    const normalizedMessage = resolveToastText(message);

    if (isTransientNetworkIssue(normalizedMessage)) {
      console.warn("[toastPolicy] Toast tecnico suprimido apos normalizacao:", normalizedMessage);
      return null;
    }

    return originalToastError(normalizedMessage, options);
  };
};

export default configureToastPolicy;
